import { PanelTypeIdentifier } from 'ui/common/enums/PanelTypeIdentifier';

export enum AtHomeTestActivationWorkflowId {
  LANDING = 'LANDING',
  BARCODE_ENTRY = 'BARCODE_ENTRY',
  ACCOUNT = 'ACCOUNT',
  TEST_SELECTION = 'TEST_SELECTION',
  TEST_ELIGIBILITY = 'TEST_ELIGIBILITY',
  TEST_INFO = 'TEST_INFO',
  PRIMARY_CARE_PROVIDER = 'PRIMARY_CARE_PROVIDER',
  PRETEST_COUNSELING = 'PRETEST_COUNSELING',
  TEST_SPECIFIC_CONSENT = 'TEST_SPECIFIC_CONSENT',
  INFORMED_DECISION_MAKING = 'INFORMED_DECISION_MAKING',
  REVIEW = 'REVIEW',
  SAMPLE_COLLECTION_INSTRUCTIONS = 'SAMPLE_COLLECTION_INSTRUCTIONS',
}

export interface IAtHomeTest {
  kit_barcode: string;
  test_type: string;
}

export interface AtHomeTestConfiguration {
  /**
   * This component gives the patient the opportunity to make an informed decision about whether to
   * continue with the test. Patients that choose not to continue should not be directed back to
   * the standard activation flow. This component is responsible for saving a record of the decision
   * to the DB if necessary.
   */
  informedDecisionMakingComponent?: React.ReactNode;

  /**
   * This component gives the patient information about the specific test to be performed, and asks
   * any additional screening questions that are not considered part of test eligibility (e.g.
   * whether the patient is currently experiencing an emergency, or what state they'll be in when
   * they collect the sample). This component is responsible for saving a record of any user input
   * to the DB if necessary.
   */
  testInfoComponent: React.ReactNode;

  /**
   * This component asks the patient for test eligibility information.
   */
  testEligibilityComponent?: React.ReactNode;

  /**
   * Pre-test counseling shown before the consent step, helping the patient understand what the test
   * can and cannot tell them and the implications of the results (currently only genetics).
   */
  pretestCounselingComponent?: React.ReactNode;

  /**
   * Additional consent for a specific test (currently only genetics).
   */
  testSpecificConsentComponent?: React.ReactNode;

  /**
   * If the test has location restrictions, this component is an error page explaining that the
   * test is unavailable if the user is in a restricted location. Typically an instance of the
   * AtHomeTestActivationUnavailableInLocation component.
   */
  testUnavailableInLocationComponent?: React.ReactNode;

  // The name of the test, to be used if the patient is eligible for multiple test types.
  testName: string;

  // A short name of the test, to be used on a CTA if the patient is eligible for multiple test types.
  testShortName: string;

  // A description of the test, to be used if the patient is eligible for multiple test types.
  testDescription: string;

  testType: PanelTypeIdentifier;
}

export interface TAvailablePanel {
  panel_type_identifier: PanelTypeIdentifier;
  population_id: string;
  program_id: string;
  order?: {
    has_physician_approval: boolean;
    status: string;
  };
}
export interface TAvailablePanelInformation {
  available_panels: TAvailablePanel[];
}
